import { NavLink } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useCart } from "@/lib/cartContext";

export default function Cart() {
  const { items, removeItem, updateQuantity, total, itemCount } = useCart();

  const shipping = total >= 150 ? 0 : 14;
  const grandTotal = total + shipping;

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-stone-50">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center gap-8 px-4 text-center">
          <ShoppingBag className="size-16 text-stone-200" />
          <div>
            <h2 className="font-semibold text-xl sm:text-2xl md:text-3xl md:font-bold text-stone-700 tracking-tight mb-3">La tua selezione è vuota</h2>
            <p className="text-stone-400 text-sm">Esplora la collezione e aggiungi i tuoi pezzi preferiti.</p>
          </div>
          <NavLink
            to="/shop"
            className="text-[10px] tracking-widest uppercase bg-stone-900 text-white px-10 py-4 hover:bg-stone-700 transition-colors flex items-center gap-2"
          >
            Esplora la collezione <ArrowRight className="size-3.5" />
          </NavLink>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <Navbar />
      <main className="flex-1">
        <div className="bg-white border-b border-stone-100 py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-600 font-semibold mb-2">Brupa</p>
            <h1 className="font-semibold text-2xl sm:text-3xl md:text-4xl md:font-bold text-stone-900 tracking-tight">
              La tua selezione <span className="text-stone-400 text-2xl">({itemCount})</span>
            </h1>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Items */}
            <div className="lg:col-span-2 space-y-6">
              {items.map(({ product, quantity }) => (
                <div key={product.id} className="bg-white p-6 flex gap-5">
                  <NavLink to={`/product/${product.id}`} className="shrink-0">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-24 h-32 object-cover bg-stone-100"
                    />
                  </NavLink>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[10px] tracking-widest uppercase text-stone-400 mb-1">{product.category}</p>
                        <NavLink to={`/product/${product.id}`}>
                          <h3 className="font-medium text-stone-900 text-sm leading-snug hover:text-stone-500 transition-colors">
                            {product.name}
                          </h3>
                        </NavLink>
                        {product.material && (
                          <p className="text-xs text-stone-400 mt-0.5">{product.material}</p>
                        )}
                      </div>
                      <button
                        onClick={() => removeItem(product.id)}
                        className="text-stone-300 hover:text-red-400 transition-colors shrink-0 p-1"
                        aria-label="Rimuovi"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>

                    <div className="mt-5 flex items-center justify-between">
                      <div className="flex items-center border border-stone-200">
                        <button
                          onClick={() => updateQuantity(product.id, quantity - 1)}
                          className="px-3 py-2 text-stone-500 hover:text-stone-900 transition-colors"
                        >
                          <Minus className="size-3.5" />
                        </button>
                        <span className="px-4 py-2 text-sm font-medium text-stone-900 border-x border-stone-200 min-w-10 text-center">
                          {quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(product.id, quantity + 1)}
                          className="px-3 py-2 text-stone-500 hover:text-stone-900 transition-colors"
                        >
                          <Plus className="size-3.5" />
                        </button>
                      </div>
                      <span className="font-medium text-stone-900">
                        €{(product.price * quantity).toLocaleString("it-IT")}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white p-8 sticky top-24">
                <h2 className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-semibold mb-8">Riepilogo ordine</h2>

                <div className="space-y-4 text-sm mb-8">
                  <div className="flex justify-between text-stone-600">
                    <span>Subtotale</span>
                    <span>€{total.toLocaleString("it-IT")}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>Spedizione</span>
                    <span className={shipping === 0 ? "text-green-600 font-medium" : ""}>
                      {shipping === 0 ? "Gratuita" : `€${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  {shipping > 0 && (
                    <p className="text-xs text-emerald-600">
                      Aggiungi €{(150 - total).toLocaleString("it-IT")} per la spedizione gratuita
                    </p>
                  )}
                  <div className="pt-4 border-t border-stone-100 flex justify-between font-semibold text-stone-900 text-base">
                    <span>Totale</span>
                    <span>€{grandTotal.toLocaleString("it-IT")}</span>
                  </div>
                </div>

                <NavLink
                  to="/checkout"
                  className="w-full py-4 bg-stone-900 text-white text-[10px] tracking-widest uppercase font-semibold flex items-center justify-center gap-2 hover:bg-stone-700 transition-colors mb-3"
                >
                  Procedi all'ordine <ArrowRight className="size-3.5" />
                </NavLink>

                <NavLink
                  to="/shop"
                  className="w-full py-3 text-[10px] tracking-widest uppercase text-stone-500 flex items-center justify-center border border-stone-200 hover:border-stone-400 transition-colors"
                >
                  Continua lo shopping
                </NavLink>

                <div className="mt-8 pt-6 border-t border-stone-100 text-center">
                  <p className="text-xs text-stone-400 mb-3">Pagamenti sicuri con</p>
                  <div className="flex justify-center gap-3 flex-wrap">
                    {["Visa", "Mastercard", "PayPal", "Apple Pay"].map((m) => (
                      <span key={m} className="text-[10px] bg-stone-50 border border-stone-200 text-stone-500 px-2.5 py-1">
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
